import React, { useState } from 'react';
import { Box, ButtonV2, DialogV2, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { ServiceCenterDialog } from '@apps/card/serviceCenter/components/ServiceCenterDialog/ServiceCenterDialog';
import type { InternalMessage, ResendOutcome } from '../../MessagesV2.types';
import { useBulkResend } from './useBulkResend';

type ResendConfirmModalProps = Readonly<{
  isOpen: boolean;
  messages: readonly InternalMessage[];
  onClose: () => void;
  /**
   * Called after a batch completes, so the table picks up the new resend rows. Carries the ids the
   * backend confirmed — the caller unticks those and keeps the rest selected, because a row that
   * did not send is a row that still needs one and the ticks are what identify it.
   */
  onCompleted: (resentIds: readonly string[]) => void;
}>;

const OutcomeMark: React.FC<{ outcome: ResendOutcome | undefined }> = ({ outcome }) => {
  const { tq } = useTranslation('messagesV2ServiceCenter');

  if (outcome === 'sent') {
    return (
      <TextV2
        fontSize="13px"
        color="positive6"
        aria-label={tq`Resent`}
      >
        ✓
      </TextV2>
    );
  }
  if (outcome === 'failed' || outcome === 'errored') {
    return (
      <TextV2
        fontSize="13px"
        color="negative6"
        aria-label={outcome === 'failed' ? tq`Failed` : tq`Not sent`}
      >
        ✕
      </TextV2>
    );
  }
  return (
    <TextV2
      fontSize="11px"
      color="mono10"
    >
      …
    </TextV2>
  );
};

/**
 * Confirmation and results in one dialog.
 *
 * Two states, deliberately not two screens: the summary the user confirmed becomes the summary
 * showing what happened, so the outcome lands where the action was taken. Reporting per row is a
 * hard requirement — resends go straight to the provider with no queue and no retry, so a failure
 * is final until someone triggers it again and CX has to know *which* rows failed. A single
 * aggregate "done" would not be enough.
 */
export const ResendConfirmModal: React.FC<ResendConfirmModalProps> = ({ isOpen, messages, onClose, onCompleted }) => {
  const { tq } = useTranslation('messagesV2ServiceCenter');
  const { outcomes, status, sentIds, sentCount, notSentCount, resend, reset } = useBulkResend();

  const isSending = status === 'sending';
  const hasRun = status !== 'idle';
  const isComplete = status === 'complete';

  /**
   * The selection as it stood when the dialog opened.
   *
   * Nothing here can render off the live `messages` prop. `onCompleted` unticks the sent rows the
   * moment a batch finishes, and the dialog stays mounted through its exit animation — `Modal`
   * keeps children while the content spring unwinds — so the summary would shed rows on the way
   * out, down to "Resend 0 messages?" when everything sent. Resetting on close has the same
   * problem, so the report is held until the next open instead.
   */
  const [session, setSession] = useState({ isOpen, messages });
  if (session.isOpen !== isOpen) {
    setSession(isOpen ? { isOpen, messages } : { ...session, isOpen });
    if (isOpen) {
      reset();
    }
  }
  const confirmed = session.messages;

  const handleClose = () => {
    // Refused mid-batch: the sends cannot be recalled, so the per-row report has to stay on screen
    // until the batch settles.
    if (isSending) {
      return;
    }
    // Refetch only if something was actually sent, so cancelling doesn't reload the table.
    if (hasRun) {
      onCompleted(sentIds);
    }
    onClose();
  };

  const title = isSending
    ? confirmed.length === 1
      ? tq`Resending 1 message…`
      : tq`Resending ${confirmed.length.toString()} messages…`
    : isComplete
      ? notSentCount === 0
        ? tq`Resent ${sentCount.toString()} of ${confirmed.length.toString()}`
        : tq`Resent ${sentCount.toString()} of ${confirmed.length.toString()} — ${notSentCount.toString()} failed`
      : confirmed.length === 1
        ? tq`Resend 1 message?`
        : tq`Resend ${confirmed.length.toString()} messages?`;

  return (
    <ServiceCenterDialog
      isOpen={isOpen}
      onClose={handleClose}
      disableCloseOnEscapeKeyClick={isSending}
      disableCloseOnOutsideClick={isSending}
      title={title}
    >
      <DialogV2.Body>
        <Box
          display="grid"
          rowGap="10px"
        >
          {!hasRun && (
            <TextV2
              typographyVariant="body1"
              color="mono10"
            >
              {tq`Each message is sent again to its original recipient. This cannot be undone.`}
            </TextV2>
          )}

          {confirmed.map(message => {
            const outcome = outcomes.get(message.id);
            return (
              <Box
                key={message.id}
                display="flex"
                alignItems="flex-start"
                gap="10px"
                paddingY="8px"
                borderBottom="1px solid #F0F0F0"
              >
                {hasRun && (
                  <Box
                    minWidth="16px"
                    display="flex"
                    justifyContent="center"
                    paddingTop="2px"
                  >
                    <OutcomeMark outcome={outcome} />
                  </Box>
                )}
                <Box style={{ minWidth: 0 }}>
                  <TextV2
                    typographyVariant="label3"
                    fontWeight={600}
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {message.messageType || tq`(no message type)`}
                  </TextV2>
                  <TextV2
                    typographyVariant="label3"
                    fontSize="11px"
                    color="mono10"
                  >
                    {message.recipient.email || message.recipient.phoneNumber} · {message.rawStatus} · {new Date(message.createAt.milliseconds).toLocaleDateString()}
                  </TextV2>
                  {outcome === 'failed' && (
                    <TextV2
                      typographyVariant="label3"
                      fontSize="11px"
                      color="negative6"
                    >
                      {/* The mutation returns only true/false. The provider's error lives on the
                          new resend row's status history, which the table already renders. */}
                      {tq`Not sent — see the new row in the table for the reason`}
                    </TextV2>
                  )}
                  {outcome === 'errored' && (
                    <TextV2
                      typographyVariant="label3"
                      fontSize="11px"
                      color="negative6"
                    >
                      {/* No row exists to point at — the call never reached the point of writing
                          one, so sending CX to the table would be a dead end. */}
                      {tq`Not sent — the request failed and no row was created. Safe to try again.`}
                    </TextV2>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </DialogV2.Body>

      <DialogV2.Footer>
        {isComplete ? (
          <ButtonV2
            type="button"
            variant="solid"
            onClick={handleClose}
          >
            {tq`Done`}
          </ButtonV2>
        ) : (
          <Box
            display="flex"
            gap="8px"
          >
            <ButtonV2
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isSending}
            >
              {tq`Cancel`}
            </ButtonV2>
            <ButtonV2
              type="button"
              variant="solid"
              disabled={isSending || confirmed.length === 0}
              onClick={() => resend(confirmed.map(message => message.id))}
            >
              {isSending ? tq`Resending…` : tq`Resend`}
            </ButtonV2>
          </Box>
        )}
      </DialogV2.Footer>
    </ServiceCenterDialog>
  );
};
